import { useCallback } from 'react';
import { Form, Formik } from 'formik';

import { Button, utils as designUtils } from '@febos-io/design';

import { useTranslation } from '../../hooks';
import {
  DATE,
  DATE_RANGE,
  IDENTIFICATION,
  MULTI_CHECK,
  MULTI_SELECT,
  SELECT,
  EMAIL,
  SELECT_PATIENT,
  SELECT_USER,
  INPUT,
  SELECT_DATA_IN_EXAMS,
  SELECT_OBJECT_IN_EXAMS
} from './input/types';
import { useModalContext } from '../../providers';
import FilterListCheck from './input/FilterListCheck';
import FilterSelect from './input/FilterSelect';
import FilterDate from './input/FilterDate';
import FilterDateRange from './input/FilterDateRange';
import FilterIdentification from './input/FilterIdentification';
import FilterEmail from './input/FilterEmail';
import FilterSelectPatient from './input/FilterSelectPatient';
import FilterSelectUser from './input/FilterSelectUser';
import FilterInput from './input/FilterInput';
import FilterSelectDataExams from './input/FilterSelectDataExams';
import FilterSelectObjectExams from './input/FilterSelectObjectExams';
import ModalDialog from '../ModalDialog';

export function ModalFilters() {
  const { closeModal, modalPayload } = useModalContext();
  const { t } = useTranslation();

  const formatValues = useCallback((filter) => {
    if (modalPayload.config.type === SELECT_USER) {
      return filter.user;
    }
    if (modalPayload.config.type === SELECT_OBJECT_IN_EXAMS) {
      return filter.object;
    }
    return filter[modalPayload.field];
  }, [modalPayload.config.type, modalPayload.field]);

  const addFilter = useCallback((filter) => {
    let values = formatValues(filter);

    if (!values || !Object.entries(values).length) {
      return designUtils.error(`${t('You must enter a')} ${modalPayload.config.name}`);
    }

    if (modalPayload.config.type === SELECT_USER) {
      values = [{
        id: filter.user?.userId,
        name: [`${filter.user?.name} ${filter.user?.lastName}, ${filter.user?.identificationNumber}`]
      }];
    }

    if (modalPayload.config.type === SELECT_PATIENT) {
      values = [{
        id: filter[modalPayload.field].id,
        name: [`${filter[modalPayload.field].name}, ${filter[modalPayload.field].identificationNumber}`]
      }];
    }

    if (modalPayload.config.type === SELECT_DATA_IN_EXAMS
      || modalPayload.config.type === SELECT_OBJECT_IN_EXAMS) {
      values = [{
        id: values.id,
        name: [values.name]
      }];
    }
    if (typeof values === 'string') {
      values = [values];
    }
    modalPayload.addFilter({
      field: modalPayload.field,
      values: values
    });
    return closeModal();
  }, [formatValues, modalPayload, closeModal, t]);

  return (
    <ModalDialog
      useBackdropClose
      useCloseModal
      size="md"
      title={`${t('Filter by')} ${t(modalPayload.config.name)}`}
    >
      <div className="modal-body">
        <Formik
          initialValues={
            {
              [modalPayload.field]: modalPayload.actualValue || []
            }
          }
          validate={values => {
            const errors = {};
            if (!values[modalPayload.field]) {
              errors[modalPayload.field] = 'Required';
            }
            return errors;
          }}
          onSubmit={addFilter}
        >
          <Form>
            <div className="mt-3">
              {modalPayload.config.type === IDENTIFICATION && (
                <FilterIdentification
                  name={modalPayload.field}
                  config={modalPayload.config}
                />
              )}
              {modalPayload.config.type === MULTI_CHECK && (
                <FilterListCheck
                  name={modalPayload.field}
                  config={modalPayload.config}
                />
              )}
              {modalPayload.config.type === MULTI_SELECT && (
                <FilterSelect
                  name={modalPayload.field}
                  config={modalPayload.config}
                  isMulti
                />
              )}
              {modalPayload.config.type === SELECT && (
                <FilterSelect
                  name={modalPayload.field}
                  config={modalPayload.config}
                />
              )}
              {modalPayload.config.type === DATE && (
                <FilterDate
                  name={modalPayload.field}
                  config={modalPayload.config}
                />
              )}
              {modalPayload.config.type === DATE_RANGE && (
                <FilterDateRange
                  name={modalPayload.field}
                  config={modalPayload.config}
                />
              )}
              {modalPayload.config.type === EMAIL && (
                <FilterEmail
                  name={modalPayload.field}
                  config={modalPayload.config}
                />
              )}
              {modalPayload.config.type === SELECT_PATIENT && (
                <FilterSelectPatient
                  name={modalPayload.field}
                  config={modalPayload.config}
                />
              )}
              {modalPayload.config.type === SELECT_USER && (
                <FilterSelectUser
                  name={modalPayload.field}
                  config={modalPayload.config}
                />
              )}
              {modalPayload.config.type === INPUT && (
                <FilterInput
                  name={modalPayload.field}
                  config={modalPayload.config}
                />
              )}
              {modalPayload.config.type === SELECT_DATA_IN_EXAMS && (
                <FilterSelectDataExams
                  name={modalPayload.field}
                  config={modalPayload.config}
                />
              )}
              {modalPayload.config.type === SELECT_OBJECT_IN_EXAMS && (
                <FilterSelectObjectExams
                  name={modalPayload.field}
                  config={modalPayload.config}
                />
              )}
            </div>
            <div className="text-end mt-3">
              <Button
                onClick={closeModal}
                size="sm"
                outline
              >
                {t('Close')}
              </Button>
              <Button
                className="ms-1"
                type="submit"
                size="sm"
              >
                {t('Add')}
              </Button>
            </div>
          </Form>
        </Formik>
      </div>
    </ModalDialog>
  );
}

export default ModalFilters;
